import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import * as moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { Annuncio } from '../models/annuncio';
import { AnnuncioAllegato } from '../models/annuncio-allegato';
import { LinkedUser, User } from '../models/user';
import { AuthService } from '../services/auth.service';
import { AnnuncioService } from '../services/annuncio.service';

@Component({
  selector: 'navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss']
})
export class NavbarComponent implements OnInit {

  @Output() logoutEvent = new EventEmitter();

  annunci: Annuncio[] = [];
  annunciOpen = false;
  annunciLoading = false;
  unreadCount = 0;

  private loadingAnnunci = false;
  markingAsRead: Record<number, boolean> = {};

  /** Flag per indicare che lo switch account è in corso */
  switchingAccount = false;

  isLoggedIn: boolean;
  user: User;
  constructor(
    private authService: AuthService,
    private annuncioService: AnnuncioService,
    private toast: ToastrService,
  ) {

    authService.loggedIn.subscribe(newValue => {
      this.isLoggedIn = newValue;
      if (!newValue) {
        this.annunciOpen = false;
        this.annunci = [];
        this.unreadCount = 0;
      }
    });

    this.user = this.authService.getLoggedInUserFromLocalStorage();

    authService.loggedInUser.subscribe(newValue => {
      this.user = new User().deserialize(newValue);
      if (this.user?.id) {
        this.loadAnnunci(true);
      }
    });


  }

  logoPath = '/imgs/peritek_logo_bianco.png';

  ngOnInit() {

    const map = {
      'peritek': '/imgs/peritek_logo_bianco.png',
      'nexus': '/imgs/nexus_logo.png',
    }

    if (this.user?.studio)
      for (let studio in map) {
        if (this.user.studio.nome.toLowerCase().includes(studio)) {
          this.logoPath = map[studio];
          break;
        }
      }

    if (this.user?.id) {
      this.loadAnnunci(true);
    }
  }

  public emitLogoutEvent() {
    this.logoutEvent.emit();
  }

  async toggleAnnunci() {
    if (this.annunciOpen) {
      this.closeAnnunci();
      return;
    }
    this.annunciOpen = true;
    await this.loadAnnunci(true);
  }

  closeAnnunci() {
    this.annunciOpen = false;
  }

  async loadAnnunci(force = false) {
    if (!this.isLoggedIn || !this.user?.id) {
      return;
    }

    if (!force && this.annunci.length) {
      this.updateUnreadCount();
      return;
    }

    if (this.loadingAnnunci) {
      return;
    }

    this.loadingAnnunci = true;
    this.annunciLoading = true;
    try {
      const annunci = await this.annuncioService.getAll(['allegati'], undefined, { cancelPrevious: false });
      this.annunci = [...annunci].sort((a, b) => {
        const bTime = b.created_at ? b.created_at.valueOf() : 0;
        const aTime = a.created_at ? a.created_at.valueOf() : 0;
        return bTime - aTime;
      });
      this.updateUnreadCount();
    } catch (error) {
      console.error(error);
      this.toast.error('Impossibile caricare gli annunci.', 'Errore');
    } finally {
      this.annunciLoading = false;
      this.loadingAnnunci = false;
    }
  }

  private updateUnreadCount() {
    this.unreadCount = this.annunci.filter(annuncio => !annuncio.isRead).length;
  }

  async onSegnaComeLetto(annuncio: Annuncio, change: MatCheckboxChange) {
    if (annuncio.isRead) {
      return;
    }

    if (!change.checked) {
      change.source.checked = false;
      return;
    }

    const annuncioId = annuncio.id;
    if (this.markingAsRead[annuncioId]) {
      return;
    }

    this.markingAsRead[annuncioId] = true;
    try {
      const response = await this.annuncioService.markAsRead(annuncio);
      annuncio.isRead = true;
      annuncio.readAt = response?.read_at ? moment(response.read_at) : moment();
      this.updateUnreadCount();
    } catch (error) {
      console.error(error);
      change.source.checked = false;
      this.toast.error('Impossibile segnare l\'annuncio come letto.', 'Errore');
    } finally {
      delete this.markingAsRead[annuncioId];
    }
  }

  openAllegato(allegato: AnnuncioAllegato) {
    if (!allegato?.id) {
      return;
    }

    const annuncio = this.annunci.find(a => a.allegati?.some(al => al.id === allegato.id));
    if (!annuncio?.id) {
      return;
    }

    this.annuncioService.downloadAllegato(annuncio.id, allegato.id).subscribe({
      next: response => {
        const blob = response.body;
        if (!blob) {
          this.toast.error('Impossibile aprire l\'allegato.', 'Errore');
          return;
        }
        const objectUrl = window.URL.createObjectURL(blob);
        window.open(objectUrl, '_blank', 'noopener');
        setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
      },
      error: err => {
        console.error(err);
        this.toast.error('Impossibile aprire l\'allegato.', 'Errore');
      }
    });
  }

  /**
   * Esegue lo switch a un account collegato
   */
  async switchAccount(linkedUser: LinkedUser) {
    if (this.switchingAccount) {
      return;
    }

    this.switchingAccount = true;
    try {
      await this.authService.switchAccount(linkedUser.id);
      // La pagina verrà ricaricata automaticamente da AuthService
    } catch (error) {
      console.error('Errore durante lo switch account:', error);
      this.toast.error('Impossibile cambiare account. Riprova.', 'Errore');
      this.switchingAccount = false;
    }
  }
}
